/**
 * ReferenceItem component for displaying URL previews in a card format
 * Shows website metadata with animated reveal effects for titles
 */
import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import parseMetadata from '../../utils/metadata';
import { Metadata } from '../../interfaces/components/linkTooltip';
import { extractDomain, getFavIcon } from '../../utils/favIcon';

/**
 * Props interface for styled components that need visibility information
 */
interface ReferenceCardProps {
  /** Number of visible items in the carousel */
  visibleItems: number;
}

/**
 * Animation for text reveal effects
 * Creates a typing-like reveal animation
 */
const reveal = keyframes`
    from {
        clip-path: inset(0 100% 0 0);
    }
    to {
        clip-path: inset(0 0 0 0);
    }
`;

// ----------------- Styled Components -----------------

/**
 * Card container for reference preview
 * Responsive sizing based on visible items count
 */
const ReferenceCard = styled(motion.a).attrs({
  layout: true,
})<ReferenceCardProps>`
  flex: 0 0 calc((100% - ${(props) => props.visibleItems - 1}rem) / ${(props) => props.visibleItems});
  height: 120px;
  scroll-snap-align: start;
  background: ${({ theme }) => theme.voiceQuestionInput.mainButtonStartColor + '22'};
  border: 1px solid ${({ theme }) => theme.text.light + '22'};
  border-radius: 12px;
  padding: 1rem;
  cursor: pointer;
  text-decoration: none;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.voiceQuestionInput.mainButtonStartColor + '33'};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

/**
 * Website favicon image
 */
const ReferenceIcon = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  object-fit: contain;
`;

/**
 * Title text with animated reveal effect
 */
const ReferenceTitle = styled.div`
  font-size: 0.875rem;
  margin-top: 0.5rem;
  overflow: hidden;
  position: relative;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  font-weight: 500;
  color: transparent; /* Hide the actual text */

  &::before,
  &::after {
    content: attr(data-text);
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    font: inherit;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
  }

  /* First layer: faded tooltip color */
  &::before {
    z-index: 2;
    color: ${({ theme }) => theme.tooltip.grayText};
    clip-path: inset(0 100% 0 0);
    animation: ${reveal} 0.4s cubic-bezier(0.61, 1, 0.88, 1) forwards;
  }

  /* Second layer: final tooltip text color */
  &::after {
    z-index: 3;
    color: ${({ theme }) => theme.tooltip.text};
    clip-path: inset(0 100% 0 0);
    animation: ${reveal} 0.7s cubic-bezier(0.61, 1, 0.88, 1) forwards;
    animation-delay: 0.5s;
  }
`;

/**
 * Fallback text for displaying URL when metadata fails to load
 */
const FallbackText = styled(motion.div)`
  font-size: 0.875rem;
  margin-top: 0.5rem;
  color: ${({ theme }) => theme.text.light};
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  font-weight: 500;
`;

/**
 * Domain text display showing website source
 */
const ReferenceDomain = styled.div`
  color: ${({ theme }) => theme.text.light + '88'};
  font-size: 0.75rem;
  margin-top: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
  font-weight: 400;
`;

/**
 * Props interface for the ReferenceItem component
 */
interface ReferenceItemProps {
  /** URL to fetch metadata from and link to */
  href: string;
  /** Number of visible items in the carousel */
  visibleItems: number;
  /** Callback triggered when the item is loaded */
  onLoad?: () => void;
}

/**
 * ReferenceItem component for displaying URL previews
 * Fetches metadata and displays website information in a card format
 *
 * @param href - URL to fetch metadata from and link to
 * @param visibleItems - Number of visible items in the carousel
 * @param onLoad - Callback triggered when the item is loaded
 * @returns Card component with website preview
 */
const ReferenceItem: React.FC<ReferenceItemProps> = ({ href, visibleItems, onLoad }) => {
  const domain = extractDomain(href);
  const [metadata, setMetadata] = useState<Metadata>({
    title: '',
    description: '',
    domain: '',
    favIcon: '',
  });

  /**
   * Fetches metadata for the URL
   * Handles success and error states
   */
  const getMetadata = async () => {
    try {
      const res = await parseMetadata(href);
      setMetadata({
        title: res.title || res.ogTitle || '',
        description: res.description || res.ogDescription || '',
        domain,
        favIcon: res.favicon,
      });
      // Call onLoad callback when metadata is loaded
      if (onLoad) onLoad();
    } catch (ex) {
      setMetadata({
        title: '',
        description: '',
        domain,
        favIcon: '',
      });
      // Still call onLoad even if metadata fetching fails
      if (onLoad) onLoad();
    }
  };

  /**
   * Fetch metadata when the component mounts or href changes
   */
  useEffect(() => {
    getMetadata();
  }, [href]);

  return (
    <ReferenceCard
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      visibleItems={visibleItems}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.5 }}
      onClick={(e) => {
        e.preventDefault();
        window.open(href, '_blank');
      }}
    >
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <ReferenceIcon
          src={getFavIcon(href)}
          onError={(e) => {
            e.currentTarget.src = `https://www.google.com/s2/favicons?sz=32&domain=${href}`;
          }}
          alt={href}
        />
        <ReferenceDomain>{domain}</ReferenceDomain>
      </div>
      {/* Using AnimatePresence to swap between fallback URL and metadata title */}
      <AnimatePresence mode="wait">
        {metadata.title ? (
          <motion.div
            key={metadata.title} // use metadata.title as key to force remount
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <ReferenceTitle data-text={metadata.title}>{metadata.title}</ReferenceTitle>
          </motion.div>
        ) : (
          <FallbackText
            key="fallback"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            {href}
          </FallbackText>
        )}
      </AnimatePresence>
    </ReferenceCard>
  );
};

export default ReferenceItem;
